import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, FileText, Save, Plus, Trash2, AlertCircle, Truck, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * PurchaseOrderImportModal
 * Two-step PO import that builds a Shipment (PO + Transport bundle):
 *  - Step 1: parse the supplier PO/invoice PDF; user edits line items.
 *  - Step 2: parse a transport invoice (PDF or photo) for the shipping cost.
 *    Can be skipped — the shipment is then flagged "transport pending".
 * On save we POST /api/coils/bulk, then POST /api/shipments to bind them.
 */
const PurchaseOrderImportModal = ({ open, onClose, onCoilsCreated }) => {
  const fileInputRef = useRef(null);
  const transportInputRef = useRef(null);
  const [step, setStep] = useState(1); // 1 = PO, 2 = Transport, 3 = Review/Save
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState(null);
  const [common, setCommon] = useState({ supplier: '', invoiceNumber: '', documentUrl: '', orderDate: '', status: 'in_stock', purchaseInvoiceTotal: '' });
  const [lineItems, setLineItems] = useState([]);

  // Transport invoice state
  const [transportFile, setTransportFile] = useState(null);
  const [parsingTransport, setParsingTransport] = useState(false);
  const [transport, setTransport] = useState({
    carrier: '', invoiceNumber: '', invoiceDate: '', total: '', documentUrl: '', notes: '',
  });
  const [transportSkipped, setTransportSkipped] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const reset = () => {
    setStep(1);
    setFile(null);
    setParsed(null);
    setLineItems([]);
    setCommon({ supplier: '', invoiceNumber: '', documentUrl: '', orderDate: '', status: 'in_stock', purchaseInvoiceTotal: '' });
    setTransportFile(null);
    setParsingTransport(false);
    setTransport({ carrier: '', invoiceNumber: '', invoiceDate: '', total: '', documentUrl: '', notes: '' });
    setTransportSkipped(false);
    setError('');
    setParsing(false);
    setSaving(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (transportInputRef.current) transportInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose?.(); };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setError('');
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/parse/purchase-order`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || 'Parse failed');
      }
      const data = await res.json();
      const p = data.parsed || {};
      setParsed(p);
      setCommon({
        supplier: p.supplier || '',
        invoiceNumber: p.invoiceNumber || '',
        documentUrl: data.documentUrl || '',
        orderDate: p.invoiceDate || '',
        status: 'in_stock',
        purchaseInvoiceTotal: (p.totals && p.totals.amount) ? String(p.totals.amount) : '',
      });
      setLineItems((p.lineItems || []).map(li => ({
        _uid: crypto.randomUUID(),
        sheetWidth: li.sheetWidth ?? '',
        thickness: li.thickness ?? '',
        weightKg: li.weightKg ?? '',
        cost: li.cost ?? '',
        coatingGsm: li.coatingGsm ?? 100,
        notes: li.notes || '',
      })));
    } catch (e) {
      setError(e.message || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const handleTransportFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setTransportFile(f);
      setError('');
    }
  };

  const handleParseTransport = async () => {
    if (!transportFile) return;
    setParsingTransport(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', transportFile);
      const res = await fetch(`${API_URL}/api/parse/transport-invoice`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || 'Transport parse failed');
      }
      const data = await res.json();
      const p = data.parsed || {};
      setTransport({
        carrier: p.carrier || '',
        invoiceNumber: p.invoiceNumber || '',
        invoiceDate: p.invoiceDate || '',
        total: p.total != null ? String(p.total) : '',
        documentUrl: data.documentUrl || '',
        notes: p.notes || '',
      });
    } catch (e) {
      setError(e.message || 'Transport parse failed');
    } finally {
      setParsingTransport(false);
    }
  };

  const updateLine = (idx, field, value) => {
    const next = [...lineItems];
    next[idx] = { ...next[idx], [field]: value };
    setLineItems(next);
  };

  const addBlankLine = () => {
    setLineItems([...lineItems, { _uid: crypto.randomUUID(), sheetWidth: '', thickness: '', weightKg: '', cost: '', coatingGsm: 100, notes: '' }]);
  };

  const removeLine = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));

  const validLineItems = () => lineItems.filter(li => li.sheetWidth && li.thickness && li.weightKg);
  const totalKg = validLineItems().reduce((s, li) => s + (parseFloat(li.weightKg) || 0), 0);
  const purchaseTotal = parseFloat(common.purchaseInvoiceTotal)
    || validLineItems().reduce((s, li) => s + (parseFloat(li.cost) || 0), 0);
  const transportTotal = parseFloat(transport.total) || 0;
  const landedPerKg = totalKg > 0 ? (purchaseTotal + transportTotal) / totalKg : 0;
  const transportPerKg = totalKg > 0 ? transportTotal / totalKg : 0;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const validRows = validLineItems();
      if (validRows.length === 0) throw new Error('At least one line item must have width, thickness, and weight');

      // 1) Save coils
      const coilPayload = {
        coils: validRows.map(li => ({
          sheetWidth: parseFloat(li.sheetWidth),
          thickness: parseFloat(li.thickness),
          weightKg: parseFloat(li.weightKg),
          cost: li.cost ? parseFloat(li.cost) : null,
          coatingGsm: li.coatingGsm ? parseFloat(li.coatingGsm) : 100,
          notes: li.notes || null,
          supplier: common.supplier || null,
          invoiceNumber: common.invoiceNumber || null,
          documentUrl: common.documentUrl || null,
          orderDate: common.orderDate || null,
          status: common.status || 'in_stock',
        })),
      };
      const coilRes = await fetch(`${API_URL}/api/coils/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(coilPayload),
      });
      if (!coilRes.ok) {
        const err = await coilRes.json().catch(() => ({ detail: `HTTP ${coilRes.status}` }));
        throw new Error(err.detail || 'Coil save failed');
      }
      const createdCoils = await coilRes.json();

      // 2) Save shipment (binds the coils + optional transport)
      const shipmentBody = {
        supplier: common.supplier || null,
        purchaseInvoiceNumber: common.invoiceNumber || null,
        purchaseInvoiceUrl: common.documentUrl || null,
        purchaseInvoiceTotal: common.purchaseInvoiceTotal ? parseFloat(common.purchaseInvoiceTotal) : null,
        purchaseInvoiceDate: common.orderDate || null,
        transportCarrier: !transportSkipped ? (transport.carrier || null) : null,
        transportInvoiceNumber: !transportSkipped ? (transport.invoiceNumber || null) : null,
        transportInvoiceUrl: !transportSkipped ? (transport.documentUrl || null) : null,
        transportInvoiceDate: !transportSkipped ? (transport.invoiceDate || null) : null,
        transportCost: !transportSkipped && transport.total ? parseFloat(transport.total) : null,
        notes: !transportSkipped ? (transport.notes || null) : null,
        coilIds: createdCoils.map(c => c.id),
      };
      const shipRes = await fetch(`${API_URL}/api/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shipmentBody),
      });
      if (!shipRes.ok) {
        const err = await shipRes.json().catch(() => ({ detail: `HTTP ${shipRes.status}` }));
        throw new Error(err.detail || 'Shipment save failed');
      }
      onCoilsCreated?.(createdCoils);
      handleClose();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ---------- step content ----------
  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 px-1 py-2 text-xs">
      {[
        { n: 1, label: 'Purchase Invoice' },
        { n: 2, label: 'Transport Invoice' },
        { n: 3, label: 'Review & Save' },
      ].map(({ n, label }, idx, arr) => (
        <React.Fragment key={n}>
          <div className={`flex items-center gap-1.5 ${step >= n ? 'text-white' : 'text-zinc-500'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= n ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-500'}`}>{n}</span>
            <span>{label}</span>
          </div>
          {idx < arr.length - 1 && <span className="text-zinc-700">→</span>}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="po-import-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 text-white">
            <Upload className="w-5 h-5 text-purple-400" />
            <h2 className="font-semibold">Import Purchase + Transport (Shipment)</h2>
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-white" data-testid="po-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-4 border-b border-zinc-800">{renderStepIndicator()}</div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* STEP 1 — PO upload + parse */}
          {step === 1 && !parsed && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-400">
                Upload the supplier PO / invoice. Accepts PDF or a photo (JPG/PNG/HEIC/WEBP). The parser will
                extract supplier, invoice number, date, and aggregate line items by dimension (width × thickness).
              </div>
              <div className="flex flex-col gap-2 items-stretch">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                  onChange={handleFileChange}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200 file:text-zinc-200 file:bg-zinc-700 file:border-0 file:px-3 file:py-1 file:rounded file:mr-3"
                  data-testid="po-import-file-input"
                />
                {file && <div className="text-xs text-zinc-500 flex items-center gap-1"><FileText className="w-3 h-3" /> {file.name} ({Math.round(file.size / 1024)} KB)</div>}
                <Button
                  onClick={handleParse}
                  disabled={!file || parsing}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="po-import-parse-btn"
                >
                  {parsing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing…</> : 'Parse'}
                </Button>
              </div>
            </div>
          )}

          {step === 1 && parsed && (
            <>
              {/* Common fields */}
              <div className="bg-zinc-800/40 border border-zinc-800 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Supplier</Label>
                  <Input value={common.supplier} onChange={e => setCommon({ ...common, supplier: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="po-common-supplier" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Invoice / PO #</Label>
                  <Input value={common.invoiceNumber} onChange={e => setCommon({ ...common, invoiceNumber: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="po-common-invoice" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Invoice Date</Label>
                  <Input type="date" value={common.orderDate} onChange={e => setCommon({ ...common, orderDate: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-zinc-400">Document URL (auto-attached to all coils)</Label>
                  <Input value={common.documentUrl} onChange={e => setCommon({ ...common, documentUrl: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="po-common-docurl" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Purchase Total (€)</Label>
                  <Input type="number" step="any" value={common.purchaseInvoiceTotal} onChange={e => setCommon({ ...common, purchaseInvoiceTotal: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" placeholder="auto = sum of coil costs" data-testid="po-common-total" />
                </div>
              </div>

              {/* Line items table */}
              <div className="bg-zinc-800/40 border border-zinc-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                  <div className="text-sm font-medium text-zinc-300">Line items ({lineItems.length})</div>
                  <Button size="sm" variant="outline" onClick={addBlankLine} className="border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                    <Plus className="w-3 h-3 mr-1" />Add row
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-800/60 text-zinc-400 text-xs">
                      <tr>
                        <th className="text-left px-2 py-1.5">Width (mm)</th>
                        <th className="text-left px-2 py-1.5">Thickness (mm)</th>
                        <th className="text-left px-2 py-1.5">Weight (kg)</th>
                        <th className="text-left px-2 py-1.5">Cost</th>
                        <th className="text-left px-2 py-1.5">Zinc (g/m²)</th>
                        <th className="text-left px-2 py-1.5">Notes</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li, idx) => (
                        <tr key={li._uid || idx} className="border-t border-zinc-800" data-testid={`po-import-row-${idx}`}>
                          <td className="px-2 py-1"><Input type="number" step="any" value={li.sheetWidth} onChange={e => updateLine(idx, 'sheetWidth', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1"><Input type="number" step="any" value={li.thickness} onChange={e => updateLine(idx, 'thickness', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1"><Input type="number" step="any" value={li.weightKg} onChange={e => updateLine(idx, 'weightKg', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1"><Input type="number" step="any" value={li.cost} onChange={e => updateLine(idx, 'cost', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1"><Input type="number" step="any" value={li.coatingGsm} onChange={e => updateLine(idx, 'coatingGsm', e.target.value)} placeholder="100" className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm w-20" data-testid={`po-import-coating-${idx}`} /></td>
                          <td className="px-2 py-1"><Input value={li.notes} onChange={e => updateLine(idx, 'notes', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1 text-right">
                            <button onClick={() => removeLine(idx)} className="text-zinc-500 hover:text-red-400" data-testid={`po-import-remove-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {lineItems.length === 0 && (
                        <tr><td colSpan={6} className="px-2 py-4 text-center text-zinc-500 text-sm">No line items extracted. Add one manually.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {parsed.totals && (parsed.totals.weightKg || parsed.totals.amount) && (
                <div className="text-xs text-zinc-500">
                  PDF totals — weight: {parsed.totals.weightKg ?? '—'} kg, amount: {parsed.totals.amount ?? '—'} {parsed.currency || ''}
                </div>
              )}
            </>
          )}

          {/* STEP 2 — Transport invoice */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-400 flex items-start gap-2">
                <Truck className="w-4 h-4 mt-0.5 text-purple-400 shrink-0" />
                <span>
                  Upload the transport / freight invoice that delivered this PO. PDF or photo (JPG/PNG) both work.
                  This cost gets allocated per kg across all the coils above so profit margins reflect the true landed cost.
                </span>
              </div>

              {!transport.total && (
                <div className="flex flex-col gap-2 items-stretch">
                  <Input
                    ref={transportInputRef}
                    type="file"
                    accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                    onChange={handleTransportFileChange}
                    className="bg-zinc-800 border-zinc-700 text-zinc-200 file:text-zinc-200 file:bg-zinc-700 file:border-0 file:px-3 file:py-1 file:rounded file:mr-3"
                    data-testid="transport-file-input"
                  />
                  {transportFile && <div className="text-xs text-zinc-500 flex items-center gap-1"><FileText className="w-3 h-3" /> {transportFile.name} ({Math.round(transportFile.size / 1024)} KB)</div>}
                  <div className="flex gap-2">
                    <Button onClick={handleParseTransport} disabled={!transportFile || parsingTransport} className="bg-purple-600 hover:bg-purple-700 text-white flex-1" data-testid="transport-parse-btn">
                      {parsingTransport ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing…</> : 'Parse Transport Invoice'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Editable fields (always visible — pre-filled if parsed) */}
              <div className="bg-zinc-800/40 border border-zinc-800 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Carrier</Label>
                  <Input value={transport.carrier} onChange={e => setTransport({ ...transport, carrier: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" placeholder="e.g. FAST TRANS DOOEL" data-testid="transport-carrier" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Transport Invoice #</Label>
                  <Input value={transport.invoiceNumber} onChange={e => setTransport({ ...transport, invoiceNumber: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" placeholder="e.g. 22-2026" data-testid="transport-invoice-number" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Date</Label>
                  <Input type="date" value={transport.invoiceDate} onChange={e => setTransport({ ...transport, invoiceDate: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Total Cost (€)</Label>
                  <Input type="number" step="any" value={transport.total} onChange={e => setTransport({ ...transport, total: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" placeholder="2300.00" data-testid="transport-total" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-zinc-400">Transport Document URL</Label>
                  <Input value={transport.documentUrl} onChange={e => setTransport({ ...transport, documentUrl: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" placeholder="Auto-set after parsing" data-testid="transport-doc-url" />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs text-zinc-400">Notes (route, CMR numbers, etc.)</Label>
                  <Input value={transport.notes} onChange={e => setTransport({ ...transport, notes: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" />
                </div>
              </div>

              {transport.total && totalKg > 0 && (
                <div className="bg-emerald-900/20 border border-emerald-900/40 rounded-lg p-3 text-sm" data-testid="landed-cost-preview">
                  <div className="text-emerald-300 font-medium mb-1">Landed cost preview</div>
                  <div className="text-xs text-zinc-300 space-y-0.5">
                    <div>Material: €{purchaseTotal.toFixed(2)} ÷ {totalKg.toFixed(0)} kg = €{(purchaseTotal / totalKg).toFixed(4)}/kg</div>
                    <div>Transport: €{transportTotal.toFixed(2)} ÷ {totalKg.toFixed(0)} kg = €{transportPerKg.toFixed(4)}/kg</div>
                    <div className="text-emerald-300 font-semibold pt-0.5">Landed total: €{landedPerKg.toFixed(4)}/kg</div>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => { setTransportSkipped(true); setStep(3); }}
                className="text-xs text-amber-300 hover:text-amber-200 underline"
                data-testid="transport-skip-btn"
              >
                Skip for now — I'll attach the transport invoice later (cost basis will be material-only until then)
              </button>
            </div>
          )}

          {/* STEP 3 — Review summary */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="bg-zinc-800/40 border border-zinc-800 rounded-lg p-3 text-sm space-y-2">
                <div className="text-zinc-300 font-medium">Shipment summary</div>
                <div className="text-xs text-zinc-400 grid grid-cols-2 gap-x-3 gap-y-1">
                  <div>Supplier:</div><div className="text-zinc-200">{common.supplier || '—'}</div>
                  <div>Purchase Invoice #:</div><div className="text-zinc-200">{common.invoiceNumber || '—'}</div>
                  <div>Coils:</div><div className="text-zinc-200">{validLineItems().length} rows · {totalKg.toFixed(0)} kg</div>
                  <div>Material cost:</div><div className="text-zinc-200">€{purchaseTotal.toFixed(2)}</div>
                  {!transportSkipped ? (
                    <>
                      <div>Transport carrier:</div><div className="text-zinc-200">{transport.carrier || '—'}</div>
                      <div>Transport invoice #:</div><div className="text-zinc-200">{transport.invoiceNumber || '—'}</div>
                      <div>Transport cost:</div><div className="text-zinc-200">{transport.total ? `€${parseFloat(transport.total).toFixed(2)}` : '—'}</div>
                    </>
                  ) : (
                    <>
                      <div>Transport:</div><div className="text-amber-300">Pending (skipped)</div>
                    </>
                  )}
                </div>
                {!transportSkipped && transport.total && totalKg > 0 && (
                  <div className="pt-2 mt-2 border-t border-zinc-800 text-sm text-emerald-300">
                    Landed cost = €{landedPerKg.toFixed(4)}/kg
                  </div>
                )}
                {transportSkipped && (
                  <div className="pt-2 mt-2 border-t border-zinc-800 text-xs text-amber-300 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                    <span>You can attach the transport invoice later from the Documents page or directly on the Raw Coils row.</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-950/30 border border-red-900/40 rounded p-2" data-testid="po-import-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-3 border-t border-zinc-800">
          <Button variant="outline" onClick={handleClose} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="step-back">
                Back
              </Button>
            )}
            {step === 1 && parsed && validLineItems().length > 0 && (
              <Button onClick={() => { setError(''); setStep(2); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="step-next-transport">
                Next: Transport <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={() => { setError(''); setTransportSkipped(false); setStep(3); }} disabled={!transport.total} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="step-next-review">
                Next: Review <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handleSave} disabled={saving || validLineItems().length === 0} className="bg-green-600 hover:bg-green-700 text-white" data-testid="po-import-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Shipment</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderImportModal;
